import instance from '../index';

const RESOURCE_SHARES = 'shares';

export const ShareApi = {
  /**
   * Create and enqueue a new share content job
   * @param {number} collectionId - Collection ID (for tracking, not required by API)
   * @param {Array} contentIds - Array of content objects with id and type (supported types: image, video)
   * @param {string} message - Optional custom share message (max 1024 characters)
   * @returns {Promise<Object>} Share job creation result
   * @property {boolean} success - Operation success status
   * @property {Object} data - Response data
   * @property {number} data.id - Job ID
   * @property {string} data.shareId - Share ID (format: shared-content-{timestamp})
   * @property {Array} data.imageIds - Array of image IDs
   * @property {Array} data.videoIds - Array of video IDs
   * @property {string} data.status - Job status (pending, processing, completed, failed)
   * @property {string} data.message - Status message
   * @property {string} data.downloadUrl - Download URL (available when status is completed)
   * @property {string} data.brandUrl - Shareable URL for public download page (format: domain/d-{shareId})
   * @property {number} data.progress - Progress percentage (0-100)
   * @property {number} data.imageCount - Number of images
   * @property {number} data.videoCount - Number of videos
   * @property {number} data.totalItems - Total number of items
   * @property {string} data.expiresAt - Job expiration date
   * @property {string} data.createdAt - Job creation date
   * @property {string} data.updatedAt - Job last updated date
   */
  enqueueShareJob(contentIds, message = null) {
    // Separate images and videos for the API call
    const imageIds = contentIds.filter(item => item.type !== 'video').map(item => item.id);
    const videoIds = contentIds.filter(item => item.type === 'video').map(item => item.id);

    const payload = {
      imageIds,
      videoIds
    };

    // Add message if provided
    if (message && message.trim()) {
      payload.message = message.trim();
    }

    return instance.post(`/${RESOURCE_SHARES}/enqueue`, payload);
  },

  /**
   * Get current status of a share job
   * @param {string} shareId - Share ID (format: shared-content-{timestamp})
   * @returns {Promise<Object>} Share job status
   * @property {boolean} success - Operation success status
   * @property {Object} data - Response data (same format as enqueue response)
   * @property {string} data.status - Job status (pending, processing, completed, failed)
   * @property {number} data.progress - Progress percentage (0-100)
   * @property {string} data.downloadUrl - Direct download URL (available when status is completed, 24-hour expiration)
   * @property {string} data.brandUrl - Shareable URL for public download page (format: domain/d-{shareId})
   * @property {boolean} data.isExpired - Whether the job has expired
   */
  getShareJobStatus(shareId) {
    return instance.get(`/${RESOURCE_SHARES}/${shareId}/status`);
  },

  //   /**
  //    * Get download information for a share ID (for the public download page)
  //    * Note: This endpoint might need to be implemented separately for public access
  //    * @param {string} shareId - Share ID from the shareable link
  //    * @returns {Promise<Object>} Download information
  //    */
  //   getDownloadInfo(shareId) {
  //     // This endpoint might need to be different for public access (no JWT required)
  //     // For now, using the same status endpoint
  //     return this.getShareJobStatus(shareId);
  //   },

  /**
   * Get share information by hash ID for public download page
   * @param {string} hashId - Hash ID from the download URL (/d-{hashId})
   * @returns {Promise<Object>} Share information
   * @property {boolean} success - Operation success status
   * @property {Object} data - Response data
   * @property {number} data.total_files - Number of files in the share
   * @property {string} data.expired_date - ISO date string when the share expires
   * @property {string|null} data.download_link - Download URL or null if expired
   */
  getShareByHashId(hashId) {
    return instance.get(`/${RESOURCE_SHARES}/${hashId}/branding`);
  },

  /**
   * Download files directly using the download URL from the share job
   * @param {string} downloadUrl - Download URL from the completed share job
   * @returns {Promise<Blob>} The download blob
   */
  async downloadFiles(downloadUrl) {
    const response = await fetch(downloadUrl, {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }

    return response.blob();
  }
};

export default ShareApi;
